import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Trash2, Loader2, Pencil, Search, BookOpen } from "lucide-react";
import { KBArticleEditor } from "./KBArticleEditor";

const CONTENT_TYPE_LABELS: Record<string, string> = {
  recipe_knowledge: "Recipe",
  business_knowledge: "Business",
  core_principle: "Core Principle",
  operational: "Operational",
  general: "General",
};

const STATUS_COLORS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  draft: "secondary",
  processing: "outline",
  error: "destructive",
};

export function KBList() {
  const [editingId, setEditingId] = useState<string | null | "new">(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");

  const { data, isLoading } = useQuery<any>({
    queryKey: ["/api/knowledge-base"],
    queryFn: () => apiRequest("GET", "/api/knowledge-base"),
  });

  const deleteMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/knowledge-base/${vars.id}`,
    method: "DELETE",
    invalidate: [["/api/knowledge-base"]],
    successMessage: "Entry deleted",
  });

  const entries = Array.isArray(data) ? data : data?.entries || [];

  const filtered = entries.filter((e: any) => {
    if (filterStatus !== "all" && e.status !== filterStatus) return false;
    if (filterType !== "all" && e.contentType !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.title?.toLowerCase().includes(q) || e.category?.toLowerCase().includes(q) || e.topic?.toLowerCase().includes(q);
    }
    return true;
  });

  // Show editor
  if (editingId !== null) {
    return <KBArticleEditor entryId={editingId === "new" ? null : editingId} onBack={() => setEditingId(null)} />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-1 w-full">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..." className="pl-9 w-full" />
          </div>
          <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="recipe_knowledge">Recipe</SelectItem>
              <SelectItem value="business_knowledge">Business</SelectItem>
              <SelectItem value="core_principle">Core Principle</SelectItem>
              <SelectItem value="operational">Operational</SelectItem>
              <SelectItem value="general">General</SelectItem>
            </SelectContent>
          </Select>
          </div>
        </div>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="h-4 w-4 mr-2" /> New Article
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardHeader className="pb-2"><CardDescription>Total</CardDescription><CardTitle className="text-2xl">{entries.length}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Active</CardDescription><CardTitle className="text-2xl text-green-600">{entries.filter((e: any) => e.status === "active").length}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Draft</CardDescription><CardTitle className="text-2xl text-yellow-600">{entries.filter((e: any) => e.status === "draft").length}</CardTitle></CardHeader></Card>
        <Card><CardHeader className="pb-2"><CardDescription>Errors</CardDescription><CardTitle className="text-2xl text-red-600">{entries.filter((e: any) => e.status === "error").length}</CardTitle></CardHeader></Card>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No articles yet</p>
            <p className="text-muted-foreground mb-4">Create your first knowledge base article to power the AI</p>
            <Button onClick={() => setEditingId("new")}><Plus className="h-4 w-4 mr-2" /> New Article</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((entry: any) => (
            <div key={entry._id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
              onClick={() => setEditingId(entry._id)}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{entry.title}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Badge variant={STATUS_COLORS[entry.status] || "secondary"} className="text-xs">{entry.status}</Badge>
                    {entry.contentType && <Badge variant="outline" className="text-xs">{CONTENT_TYPE_LABELS[entry.contentType] || entry.contentType}</Badge>}
                    {entry.category && <span className="text-xs text-muted-foreground">{entry.category}</span>}
                    {entry.type === "document" && <Badge variant="outline" className="text-xs">Document</Badge>}
                    {entry.ragTargets?.map((t: string) => (
                      <Badge key={t} variant="outline" className="text-xs">{t === "assistant" ? "🤖 Assistant" : "👨‍🍳 Sous Chef"}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground hidden sm:block">
                  {entry.chunksCount || 0} chunks
                </span>
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEditingId(entry._id); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate({ id: entry._id }); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
